import { COOKIE_KEY_COOKIE_CONSENT } from '@application/constants';
import { clearCookieFromAllPathsAndDomains, cookies, useModal } from '@application/utils';
import type {
  CookiePreferencesFormValues,
  CookiePreferencesModalItem,
} from '@presentation/components';
import { CookiePreferencesModal } from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import dayjs from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import { singletonHook } from 'react-singleton-hook';

const init: ReturnType<typeof useCookiePreferencesModalSingleton> = {
  modalHook: {
    show: () => undefined,
    showFromClick: () => undefined,
    close: () => undefined,
    modal: {
      onCloseClick: undefined,
      open: false,
      position: [0, 0],
    },
  },
};

export const useCookiePreferencesModalSingleton = () => {
  const modalHook = useModal({});

  return modalHook;
};

export const useCookiePreferencesModal = singletonHook(init, useCookiePreferencesModalSingleton);

interface CookiePreferencesModalControllerProps {
  // eslint-disable-next-line react/require-default-props
  closeCookieBanner?: () => void;
}
export const CookiePreferencesModalController = (props: CookiePreferencesModalControllerProps) => {
  const { closeCookieBanner } = props;
  const { modal, close } = useCookiePreferencesModal();
  const { translate } = useTranslation();
  const initialValues: CookiePreferencesFormValues = useMemo(() => {
    const cookieConsent = cookies.get(COOKIE_KEY_COOKIE_CONSENT);
    return {
      necessary: true, // Necessary cookies are always active
      analytics: cookieConsent ? cookieConsent[1] !== '0' : true,
    };
  }, []);

  const onSubmitHandler = useCallback(
    (values: CookiePreferencesFormValues) => {
      if (typeof document !== 'undefined') {
        const expirationDate = dayjs().add(1, 'year').toDate().toUTCString();
        const cookieConsent = `1${values.analytics === true ? '1' : '0'}`;

        document.cookie = `${COOKIE_KEY_COOKIE_CONSENT}=${cookieConsent}; expires=${expirationDate}`;
        if (values.analytics === false) {
          clearCookieFromAllPathsAndDomains('_ga');
          clearCookieFromAllPathsAndDomains('_gat');
          clearCookieFromAllPathsAndDomains('_gid');
          window.gtag('consent', 'update', {
            analytics_storage: 'denied',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
          });
        } else {
          window.gtag('consent', 'update', {
            analytics_storage: 'granted',
            ad_storage: 'granted',
            ad_user_data: 'granted',
            ad_personalization: 'granted',
          });
        }
      }
      close();
      closeCookieBanner?.();
    },
    [close, closeCookieBanner],
  );

  const form = useFormik<CookiePreferencesFormValues>({ initialValues, onSubmit: onSubmitHandler });

  const items = useMemo(() => {
    return [
      {
        id: '1',
        title: translate('cookiePreferences.categories.necessary.title'),
        content: translate('cookiePreferences.categories.necessary.text'),
        name: 'necessary',
      },
      {
        id: '2',
        title: translate('cookiePreferences.categories.analytics.title'),
        content: translate('cookiePreferences.categories.analytics.text'),
        name: 'analytics',
      },
    ] as CookiePreferencesModalItem[];
  }, [translate]);

  const AllowAllClickHandler = useCallback(() => {
    form.setValues({ analytics: true, necessary: true });
    form.submitForm();
  }, [form]);

  const SaveClickHandler = useCallback(() => {
    form.submitForm();
  }, [form]);

  return (
    <FormikProvider value={form}>
      <CookiePreferencesModal
        modal={modal}
        items={items}
        onAllowAllClick={AllowAllClickHandler}
        onSaveClick={SaveClickHandler}
      />
    </FormikProvider>
  );
};
